import { Checkbox } from "@xola/ui-kit";
import { sumBy, uniqueId } from "lodash";
import React, { useState } from "react";
import { useSearch } from "../../../hooks/useSearch";
import { useSelection } from "../../../hooks/useSelection";
import { FilterMenuButton } from "../../Dashboard/Filters/FilterMenuButton";
import { Loader } from "../../Loader";
import { MultiSelectPopover } from "../../MultiSelectPopover";
import { ReportColumnDef } from "./SuggestedReports";

interface ColumnSelectorProps {
    columns: ReportColumnDef[];
    excludedColumns: string[];
    onChange: (excludedColumns: string[]) => void;
}

export const ColumnSelector = ({ columns, excludedColumns, onChange }: ColumnSelectorProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [search, setSearch] = useState("");
    const items = columns.map((column) => {
        const value = { id: column.field, headerName: column.headerName, hide: column.hide };
        if (column.parent) {
            return { ...value, parent: { id: column.parent.field, headerName: column.parent.headerName } };
        }

        return value;
    });

    const filteredItems = useSearch(items, (colDef) => [colDef.headerName], search);
    const isFiltered = search.length > 0 && filteredItems.length < items.length;

    const isAllSelected = excludedColumns.length === 0;
    const selectedColumns = columns
        .filter((column) => !column.parent && !excludedColumns.includes(column.field))
        .map((column) => column.field);
    const selected = isAllSelected ? [] : selectedColumns;
    const selection = useSelection(filteredItems, isFiltered, { all: isAllSelected, selected }, false);

    const toggleIsVisible = () => {
        setIsVisible(!isVisible);
    };

    const handleApply = () => {
        onChange(getExcludedColumns(selection, columns));
        setIsVisible(false);
        setSearch("");
    };

    const handleCancel = () => {
        setIsVisible(false);
        setSearch("");
    };

    const handleSearch = (text: string) => {
        setSearch(text.trim());
    };

    const selectedCount = sumBy(filteredItems, (item) => {
        return "parent" in item || item.hide ? 0 : selection.isSelected(item) ? 1 : 0;
    });

    return (
        <MultiSelectPopover
            title="Select Columns"
            isVisible={isVisible}
            target={
                <FilterMenuButton
                    isDropdown
                    text={
                        <>
                            <var data-var="selectedCount">{selectedCount}</var> Columns
                        </>
                    }
                    onClick={toggleIsVisible}
                />
            }
            selectedCount={selectedCount}
            onApply={handleApply}
            onCancel={handleCancel}
            onSearch={handleSearch}
        >
            {columns.length === 0 ? (
                <Loader text="Fetching columns..." />
            ) : (
                <>
                    <MultiSelectPopover.Item key="select-all" className="bg-gray-lighter">
                        <div className="flex w-full justify-between">
                            <Checkbox
                                checked={selection.isSelectedAll}
                                label="Select All"
                                onChange={() => selection.selectAll()}
                            />
                        </div>
                    </MultiSelectPopover.Item>

                    {filteredItems.map((column) => {
                        const id = column.id ?? uniqueId(column.headerName.toLowerCase().replace(/[^a-z\d]/gi, "-"));
                        if ("parent" in column || column.hide) {
                            return null;
                        }

                        return (
                            <MultiSelectPopover.Item key={id}>
                                <Checkbox
                                    checked={selection.isSelected(column)}
                                    label={column.headerName}
                                    onChange={() => selection.select(column)}
                                />
                            </MultiSelectPopover.Item>
                        );
                    })}
                </>
            )}
        </MultiSelectPopover>
    );
};

const getExcludedColumns = (selection: ReturnType<typeof useSelection>, columns: ReportColumnDef[]) => {
    if (selection.isSelectedAll) {
        return [];
    }

    const { selected } = selection.state;
    const excludedColumnDefinitions = columns.filter((column) => {
        const shouldExclude = !selected.includes(column.field);
        if (column.parent) {
            return shouldExclude && !selected.includes(column.parent.field);
        }

        return shouldExclude;
    });

    return excludedColumnDefinitions.map((column) => column.field);
};
